<template>
  <transition name="fade">
    <div v-if="visible" class="cd-overlay" :style="{ zIndex: zIndex }" role="dialog" aria-live="assertive">
      <div class="cd-wrap" :style="{ width: sizePx, height: sizePx }">
        <!-- 旋转圆形进度条：每秒转一圈 -->
        <div
          class="cd-spinner"
          :style="{
            width: sizePx,
            height: sizePx,
            animationPlayState: spinning ? 'running' : 'paused',
            animationDuration: '1s'
          }"
          aria-hidden="true"
        >
          <div class="cd-track" :style="trackStyle"></div>
          <div class="cd-head" :style="headStyle"></div>
        </div>

        <!-- 数字 -->
        <div class="cd-number" :style="{ color: textColor, fontSize: numberFontSize }">
          {{ display }}
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
/**
 * Countdown321.vue (Vue 2)
 *
 * 用法：
 * <Countdown321 ref="countdown" :size="240" :color="'#22c55e'" @finished="onGoLive"/>
 * this.$refs.countdown.start(); // 点击“开播”后调用
 *
 * Props:
 * - size: 直径（px）
 * - color: 进度条颜色
 * - trackColor: 轨道颜色
 * - textColor: 数字颜色
 * - zIndex: 遮罩层级
 * - seconds: 倒计时秒数（默认 3）
 * - autoStart: 是否挂载后自动开始
 *
 * Events:
 * - finished: 倒计时完成后触发
 */
export default {
  name: "Countdown321",
  props: {
    size: { type: Number, default: 220 },
    color: { type: String, default: "#eee" },        // 进度条颜色（默认绿色）
    trackColor: { type: String, default: "rgba(255,255,255,0.18)" },
    textColor: { type: String, default: "#ffffff" },
    zIndex: { type: Number, default: 2147483000 },
    seconds: { type: Number, default: 5 },              // 3→2→1
    autoStart: { type: Boolean, default: false }
  },
  data() {
    return {
      visible: false,
      spinning: false,
      display: 3,
      timer: null,
      ticks: 0
    };
  },
  computed: {
    sizePx() {
      return `${this.size}px`;
    },
    numberFontSize() {
      // 数字大小随直径自适应
      return `${Math.max(24, Math.floor(this.size * 0.36))}px`;
    },
    trackStyle() {
      return {
        borderColor: this.trackColor
      };
    },
    headStyle() {
      return {
        background: this.color
      };
    }
  },
  mounted() {
    if (this.autoStart) this.start();
  },
  beforeDestroy() {
    this.stopTimers();
  },
  methods: {
    start() {
      this.stopTimers();
      this.display = this.seconds;
      this.visible = true;
      this.spinning = true;
      this.ticks = 0;

      // 每秒旋转一圈，数字同时减 1
      this.timer = setInterval(() => {
        this.ticks += 1;
        if (this.display > 1) {
          this.display -= 1;
        } else {
          // 最后一圈结束，收尾
          this.finish();
        }
      }, 1000);
    },
    finish() {
      this.stopTimers();
      // 小延时让当前帧旋转补完更自然
      setTimeout(() => {
        this.spinning = false;
        this.visible = false;
        this.$emit("finished");
      }, 20);
    },
    cancel() {
      // 若你想提前取消
      this.stopTimers();
      this.spinning = false;
      this.visible = false;
    },
    stopTimers() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }
  }
};
</script>

<style scoped>
/* 遮罩层 & 过渡 */
.cd-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  display: grid;
  place-items: center;
  backdrop-filter: blur(2px);
}
.fade-enter-active, .fade-leave-active { transition: opacity .18s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }

/* 容器 */
.cd-wrap {
  position: relative;
  display: grid;
  place-items: center;
}

/* 旋转进度条容器：每秒 360° */
.cd-spinner {
  position: relative;
  border-radius: 50%;
  animation-name: cd-rotate;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  will-change: transform;
}

/* 背景轨道 */
.cd-track {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: calc(0.065 * var(--diameter, 220px)) solid rgba(255,255,255,0.18);
}

/* 进度条的“指针小尾巴” */
.cd-head {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 5px;
  height: 5px;
  transform-origin: -210% 50%; /* 让小圆点远离中心（形成尾巴） */
  border-radius: 999px;
  /* 通过阴影拉出尾巴轨迹 */
  box-shadow:
    12px 0 0 0 currentColor,
    24px 0 0 0 currentColor,
    36px 0 0 0 currentColor,
    48px 0 0 0 currentColor,
    60px 0 0 0 currentColor,
    72px 0 0 0 currentColor,
    84px 0 0 0 currentColor,
    96px 0 0 0 currentColor;
  color: #dbe8e4; /* 被 JS 覆盖为 props.color */
}

/* 大数字 */
.cd-number {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  font-weight: 800;
  letter-spacing: 1px;
  text-shadow: 0 6px 18px rgba(0,0,0,0.35);
  user-select: none;
}

/* 旋转动画 */
@keyframes cd-rotate {
  from { transform: rotate(0turn); }
  to   { transform: rotate(1turn); }
}

/* 自适应：用 CSS 变量把 size 传给边框厚度计算 */
.cd-wrap { --diameter: 220px; }
</style>
